import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinkIcon from '@mui/icons-material/Link';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HistoryIcon from '@mui/icons-material/History';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  getProjects,
  getGoogleSheetsStatus,
  getGoogleSheetsSettings,
  linkGoogleSheet,
  exportProjectToGoogleSheets,
  exportAllToGoogleSheets,
  getGoogleSheetsHistory,
} from '../api';

interface Project {
  id: number;
  name: string;
  project_id: number;
  is_active: boolean;
}

interface ProjectWithSheet extends Project {
  spreadsheetId: string | null;
  spreadsheetUrl: string | null;
  spreadsheetName: string | null;
}

interface ExportLog {
  id: number;
  project_id: number;
  project_name: string;
  spreadsheet_id: string;
  sheet_name: string;
  leads_exported: number;
  leads_skipped: number;
  success: boolean;
  error_message: string | null;
  execution_time_ms: number;
  created_at: string;
}

export const GoogleSheetsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [projects, setProjects] = useState<ProjectWithSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<{ connected: boolean; message: string } | null>(null);
  const [exportHistory, setExportHistory] = useState<ExportLog[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  
  // Dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectWithSheet | null>(null);
  const [spreadsheetIdInput, setSpreadsheetIdInput] = useState('');
  
  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportAllLeads, setExportAllLeads] = useState(false);
  const [projectToExport, setProjectToExport] = useState<ProjectWithSheet | null>(null);
  
  // Export state
  const [exportingProject, setExportingProject] = useState<number | null>(null);
  const [exportingAll, setExportingAll] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const SERVICE_ACCOUNT_EMAIL = 'leadmanager-sheets@wired-episode-480307-q5.iam.gserviceaccount.com';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [projectsData, statusData] = await Promise.all([
        getProjects(),
        getGoogleSheetsStatus().catch(() => ({ connected: false, message: 'Ошибка подключения' })),
      ]);

      setConnectionStatus(statusData);

      // Получаем настройки Google Sheets для каждого проекта
      const projectsWithSheets = await Promise.all(
        projectsData.map(async (project: Project) => {
          try {
            const settings = await getGoogleSheetsSettings(project.id);
            return {
              ...project,
              spreadsheetId: settings.spreadsheetId,
              spreadsheetUrl: settings.spreadsheetUrl,
              spreadsheetName: settings.spreadsheetName,
            };
          } catch {
            return {
              ...project,
              spreadsheetId: null,
              spreadsheetUrl: null,
              spreadsheetName: null,
            };
          }
        })
      );

      setProjects(projectsWithSheets);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const history = await getGoogleSheetsHistory(undefined, 100);
      setExportHistory(history);
    } catch (error: any) {
      console.error('Failed to fetch history:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchHistory();
  }, [fetchData, fetchHistory]);

  const handleLinkSheet = async () => {
    if (!selectedProject || !spreadsheetIdInput.trim()) return;

    try {
      // Извлекаем ID из URL если передан полный URL
      let sheetId = spreadsheetIdInput.trim();
      const urlMatch = sheetId.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
      if (urlMatch) {
        sheetId = urlMatch[1];
      }

      const result = await linkGoogleSheet(selectedProject.id, sheetId);
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setLinkDialogOpen(false);
        setSpreadsheetIdInput('');
        fetchData();
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const openExportDialog = (project: ProjectWithSheet) => {
    setProjectToExport(project);
    setExportAllLeads(false);
    setExportDialogOpen(true);
  };

  const handleExportProject = async () => {
    if (!projectToExport) return;
    
    setExportDialogOpen(false);
    setExportingProject(projectToExport.id);
    
    try {
      const result = await exportProjectToGoogleSheets(projectToExport.id, exportAllLeads);
      setSnackbar({
        open: true,
        message: result.message,
        severity: result.success ? 'success' : 'error',
      });
      fetchHistory();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setExportingProject(null);
      setProjectToExport(null);
    }
  };

  const handleExportAll = async () => {
    setExportingAll(true);
    try {
      const result = await exportAllToGoogleSheets();
      setSnackbar({
        open: true,
        message: `Экспортировано ${result.totalLeadsExported} лидов из ${result.successfulProjects}/${result.totalProjects} проектов`,
        severity: result.successfulProjects > 0 ? 'success' : 'error',
      });
      fetchHistory();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setExportingAll(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Скопировано в буфер обмена', severity: 'info' });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Box mb={3} sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center',
        gap: 2
      }}>
        <Typography variant={isMobile ? "h5" : "h4"}>Google Sheets Экспорт</Typography>
        <Box display="flex" gap={2} sx={{ width: isMobile ? '100%' : 'auto' }}>
          <IconButton 
            onClick={() => { fetchData(); fetchHistory(); }} 
            title="Обновить"
            sx={{ display: isMobile ? 'none' : 'inline-flex' }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            color="primary"
            startIcon={exportingAll ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleExportAll}
            disabled={exportingAll}
            fullWidth={isMobile}
          >
            Экспорт всех проектов
          </Button>
        </Box>
      </Box>

      {/* Connection Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box display="flex" alignItems="flex-start" gap={2}>
            {connectionStatus?.connected ? (
              <CheckCircleIcon color="success" sx={{ mt: 0.5 }} />
            ) : (
              <ErrorIcon color="error" sx={{ mt: 0.5 }} />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Статус подключения Google API
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {connectionStatus?.message || 'Проверка...'}
              </Typography>
            </Box>
            {isMobile && (
              <IconButton size="small" onClick={() => { fetchData(); fetchHistory(); }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              Email сервисного аккаунта (добавьте как редактора в Google таблицу):
            </Typography>
            <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'stretch' : 'center'} gap={1}>
              <Typography
                variant="body2"
                fontFamily="monospace"
                sx={{ 
                  wordBreak: 'break-all',
                  bgcolor: '#fff',
                  p: 1,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  fontSize: '0.8rem'
                }}
              >
                {SERVICE_ACCOUNT_EMAIL}
              </Typography>
              <Button size="small" variant="outlined" onClick={() => copyToClipboard(SERVICE_ACCOUNT_EMAIL)}>
                Копировать
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="Проекты" />
        <Tab label="История экспорта" icon={<HistoryIcon />} iconPosition="start" />
      </Tabs>

      {activeTab === 0 && (
        <>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {projects.map((project) => (
                <Card key={project.id} variant="outlined">
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {project.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {project.id} | Ext ID: {project.project_id}
                        </Typography>
                      </Box>
                      <Chip 
                        label={project.spreadsheetUrl ? "Настроено" : "Не настроено"} 
                        color={project.spreadsheetUrl ? "success" : "default"} 
                        size="small" 
                      />
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="textSecondary" display="block">Google Таблица:</Typography>
                      {project.spreadsheetUrl ? (
                        <Box sx={{ mt: 0.5 }}>
                          <Button
                            size="small"
                            href={project.spreadsheetUrl}
                            target="_blank"
                            startIcon={<OpenInNewIcon />}
                            variant="text"
                            sx={{ p: 0, minWidth: 0, textAlign: 'left', display: 'flex', justifyContent: 'flex-start' }}
                          >
                            <Typography variant="body2" sx={{ 
                              maxWidth: '200px', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              textTransform: 'none'
                            }}>
                              {project.spreadsheetName || 'Открыть таблицу'}
                            </Typography>
                          </Button>
                          <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
                            ID: {project.spreadsheetId?.slice(0, 20)}...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Не привязана
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<LinkIcon />}
                        onClick={() => {
                          setSelectedProject(project);
                          setSpreadsheetIdInput(project.spreadsheetId || '');
                          setLinkDialogOpen(true);
                        }}
                        sx={{ flex: 1 }}
                      >
                        {project.spreadsheetUrl ? 'Изменить' : 'Привязать'}
                      </Button>
                      {project.spreadsheetUrl && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={
                            exportingProject === project.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <CloudUploadIcon />
                            )
                          }
                          onClick={() => openExportDialog(project)}
                          disabled={exportingProject === project.id}
                          sx={{ flex: 1 }}
                        >
                          Экспорт
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {!projects.length && (
                <Typography align="center" color="textSecondary" sx={{ py: 2 }}>
                  Проекты не найдены
                </Typography>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Проект</TableCell>
                    <TableCell>Google Таблица</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>{project.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {project.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          External ID: {project.project_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {project.spreadsheetUrl ? (
                          <Box>
                            <Tooltip title={project.spreadsheetName || 'Открыть таблицу'}>
                              <Button
                                size="small"
                                href={project.spreadsheetUrl}
                                target="_blank"
                                startIcon={<OpenInNewIcon />}
                              >
                                {project.spreadsheetName
                                  ? project.spreadsheetName.length > 30
                                    ? `${project.spreadsheetName.slice(0, 30)}...`
                                    : project.spreadsheetName
                                  : 'Открыть'}
                              </Button>
                            </Tooltip>
                            <Typography variant="caption" display="block" color="textSecondary">
                              ID: {project.spreadsheetId?.slice(0, 20)}...
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Не привязана
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {project.spreadsheetUrl ? (
                          <Chip label="Настроено" color="success" size="small" />
                        ) : (
                          <Chip label="Не настроено" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LinkIcon />}
                            onClick={() => {
                              setSelectedProject(project);
                              setSpreadsheetIdInput(project.spreadsheetId || '');
                              setLinkDialogOpen(true);
                            }}
                          >
                            {project.spreadsheetUrl ? 'Изменить' : 'Привязать'}
                          </Button>
                          {project.spreadsheetUrl && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={
                                exportingProject === project.id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <CloudUploadIcon />
                                )
                              }
                              onClick={() => openExportDialog(project)}
                              disabled={exportingProject === project.id}
                            >
                              Экспорт
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {activeTab === 1 && (
        isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {exportHistory.length === 0 ? (
              <Typography align="center" color="textSecondary" sx={{ py: 2 }}>
                История экспорта пуста
              </Typography>
            ) : (
              exportHistory.map((log) => (
                <Card key={log.id} variant="outlined">
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {formatDate(log.created_at)}
                      </Typography>
                      {log.success ? (
                        <Chip label="Успешно" color="success" size="small" icon={<CheckCircleIcon />} />
                      ) : (
                        <Chip label="Ошибка" color="error" size="small" icon={<ErrorIcon />} />
                      )}
                    </Box>

                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      Проект: {log.project_name || `ID: ${log.project_id}`}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Лист: {log.sheet_name || '—'}
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Экспортировано:</Typography>
                        <Typography variant="body2" sx={{ color: log.leads_exported > 0 ? 'success.main' : 'inherit', fontWeight: log.leads_exported > 0 ? 'bold' : 'normal' }}>
                          {log.leads_exported}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Пропущено:</Typography>
                        <Typography variant="body2">{log.leads_skipped}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Время:</Typography>
                        <Typography variant="body2">{log.execution_time_ms} мс</Typography>
                      </Box>
                    </Box>

                    {!log.success && log.error_message && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#fff5f5', borderRadius: 1, border: '1px solid #ffcfcf' }}>
                        <Typography variant="caption" color="error" sx={{ wordBreak: 'break-all' }}>
                          {log.error_message}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>Проект</TableCell>
                  <TableCell>Лист</TableCell>
                  <TableCell>Экспортировано</TableCell>
                  <TableCell>Пропущено</TableCell>
                  <TableCell>Время (мс)</TableCell>
                  <TableCell>Статус</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exportHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary">История экспорта пуста</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  exportHistory.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{formatDate(log.created_at)}</TableCell>
                      <TableCell>{log.project_name || `ID: ${log.project_id}`}</TableCell>
                      <TableCell>{log.sheet_name || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.leads_exported}
                          size="small"
                          color={log.leads_exported > 0 ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{log.leads_skipped}</TableCell>
                      <TableCell>{log.execution_time_ms}</TableCell>
                      <TableCell>
                        {log.success ? (
                          <Chip label="Успешно" color="success" size="small" icon={<CheckCircleIcon />} />
                        ) : (
                          <Tooltip title={log.error_message || 'Ошибка'}>
                            <Chip label="Ошибка" color="error" size="small" icon={<ErrorIcon />} />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Привязать Google таблицу к проекту "{selectedProject?.name}"
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Инструкция:</strong>
            </Typography>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>Создайте новую Google таблицу или откройте существующую</li>
              <li>
                Нажмите "Поделиться" и добавьте email:
                <br />
                <code style={{ fontSize: '0.85em' }}>{SERVICE_ACCOUNT_EMAIL}</code>
                <br />
                с правами <strong>"Редактор"</strong>
              </li>
              <li>Скопируйте ID таблицы из URL и вставьте ниже</li>
            </ol>
          </Alert>
          <TextField
            fullWidth
            label="ID или URL таблицы"
            placeholder="1ABC123xyz... или https://docs.google.com/spreadsheets/d/1ABC123xyz.../edit"
            value={spreadsheetIdInput}
            onChange={(e) => setSpreadsheetIdInput(e.target.value)}
            helperText="Введите ID таблицы или полный URL"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleLinkSheet}
            disabled={!spreadsheetIdInput.trim()}
          >
            Привязать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Экспорт лидов проекта "{projectToExport?.name}"
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportAllLeads}
                  onChange={(e) => setExportAllLeads(e.target.checked)}
                />
              }
              label="Выгрузить все лиды"
            />
            <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
              {exportAllLeads 
                ? 'Все лиды проекта будут выгружены на отдельный лист "all"'
                : `Лиды за текущий месяц будут выгружены на лист "${new Date().toLocaleDateString('ru-RU', { month: '2-digit', year: '2-digit' }).replace('/', '.')}"`
              }
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleExportProject}
            startIcon={<CloudUploadIcon />}
          >
            Экспорт
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

